import type { ZodType, ZodTypeDef } from 'zod';
import { dataLatestPath } from '@hud/core';
import { useVaultText } from './useStore.js';
import { useNow } from './useNow.js';

export type SourceState<T> =
  /** No fetch has landed yet — usually the schedule hasn't run since the source was added. */
  | { status: 'missing'; path: string }
  /** The file exists but isn't what this widget expects. */
  | { status: 'invalid'; path: string; error: string }
  | { status: 'ready'; path: string; data: T; ageMinutes: number; stale: boolean };

/**
 * Read a scheduled source's latest payload out of the store.
 *
 * Synchronous, because the store already holds every text file — a data tile
 * has no loading state and works offline for the same reason every other tile
 * does.
 *
 * Staleness is computed rather than trusted. The failure mode of a scheduled
 * fetch is a file that still exists and still parses while the job behind it
 * has been broken for a day, so age is derived from `fetchedAt` and surfaced to
 * the caller instead of being assumed fresh.
 */
export function useSourceData<T>(
  source: string,
  schema: ZodType<T, ZodTypeDef, unknown>,
  staleAfterMinutes: number,
): SourceState<T> {
  const path = dataLatestPath(source);
  const raw = useVaultText(path);
  // Re-render on the minute so the age label and stale flag stay honest.
  const now = useNow(60_000);

  if (raw === undefined) return { status: 'missing', path };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return { status: 'invalid', path, error: err instanceof Error ? err.message : String(err) };
  }

  const result = schema.safeParse(parsed);
  if (!result.success) {
    return {
      status: 'invalid',
      path,
      error: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    };
  }

  const data = result.data as T & { fetchedAt: string };
  const fetchedAt = Date.parse(data.fetchedAt);
  const ageMinutes = Number.isNaN(fetchedAt)
    ? Number.POSITIVE_INFINITY
    : Math.max(0, Math.round((now.getTime() - fetchedAt) / 60_000));

  return {
    status: 'ready',
    path,
    data: result.data,
    ageMinutes,
    stale: ageMinutes > staleAfterMinutes,
  };
}

/** "just now", "14m ago", "3h ago", "2d ago". */
export function formatAge(minutes: number): string {
  if (!Number.isFinite(minutes)) return 'unknown age';
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / (60 * 24))}d ago`;
}
